import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, InputAdornment } from '@mui/material';
import _ from 'lodash';

import { Page } from 'ui/components/Page/Page';
import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import { reportList } from 'services/reports';
import { fetchLocations } from 'services/locations';
import { fetchTags } from 'services/tags';
import { fetchRepresentatives } from 'services/representatives';
import { fetchVendors } from 'services/vendors';
import { useUrlQueryObject } from 'services/url';

import { ReportsDetailsCard } from './components/ReportsDetailsCard';
import { ReportsPageTable } from './components/ReportsPageTable';
import { ReportsPagePageCmp, ReportsPageProps } from './types';
import { Routes } from '../../navigation';
import { getActiveUser } from 'services/user';
import { hasPermission } from 'services/permissions';
import { useFeatureAccess } from 'helpers/serviceTierHooks';
import { colorPalette, icons } from 'ui/theme';
import { FBOSearchInput } from 'ui/theme/components/FBOSearchHeader/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { concatRoute } from 'helpers/routes';

// Report IDs as named constants - immutable and type-safe
const REPORT_IDS = {
  // Sales Reports
  SALES_ORDER: 2,
  CUSTOMER_HISTORY: 4,
  ITEM_PRICE_LIST: 6,
  PICK: 7,
  PACKING_LIST: 9,
  SHIPPING_INVOICE: 10,
  TOTAL_ITEM_SALES: 12,
  SALES_ORDER_SUMMARY: 16,
  MARGIN: 25,

  // Purchasing Reports
  PURCHASE_ORDER: 8,
  RECEIVING_LIST: 11,
  REORDER: 13,
  PURCHASE_ORDER_SUMMARY: 15,
  VENDOR_FULFILLMENT_HISTORY: 24,
  BILL_OF_LADING: 27,
} as const;

// Type for individual report IDs
type ReportId = (typeof REPORT_IDS)[keyof typeof REPORT_IDS];

const REPORT_CATEGORIES: {
  SALES: Set<ReportId>;
  PURCHASING: Set<ReportId>;
} = {
  SALES: new Set([
    REPORT_IDS.SALES_ORDER,
    REPORT_IDS.CUSTOMER_HISTORY,
    REPORT_IDS.ITEM_PRICE_LIST,
    REPORT_IDS.PICK,
    REPORT_IDS.PACKING_LIST,
    REPORT_IDS.SHIPPING_INVOICE,
    REPORT_IDS.TOTAL_ITEM_SALES,
    REPORT_IDS.SALES_ORDER_SUMMARY,
    REPORT_IDS.MARGIN,
  ]),
  PURCHASING: new Set([
    REPORT_IDS.PURCHASE_ORDER,
    REPORT_IDS.RECEIVING_LIST,
    REPORT_IDS.REORDER,
    REPORT_IDS.PURCHASE_ORDER_SUMMARY,
    REPORT_IDS.VENDOR_FULFILLMENT_HISTORY,
    REPORT_IDS.BILL_OF_LADING,
  ]),
};

// Use the actual feature access type from the hook
type ReportFeatureAccess = Pick<
  ReturnType<typeof useFeatureAccess>,
  'canAccessSales' | 'canAccessPurchasing' | 'canViewReorderReport'
>;

/**
 * Check if a report belongs to the sales category
 */
const isSalesReport = (reportId: number): boolean => {
  return REPORT_CATEGORIES.SALES.has(reportId as ReportId);
};

/**
 * Check if a report belongs to the purchasing category
 */
const isPurchasingReport = (reportId: number): boolean => {
  return REPORT_CATEGORIES.PURCHASING.has(reportId as ReportId);
};

/**
 * Check if user has feature access for a specific report category
 */
const hasFeatureAccessForReport = (
  reportId: number,
  featureAccess: ReportFeatureAccess
): boolean => {
  if (reportId === REPORT_IDS.REORDER && featureAccess.canViewReorderReport) {
    return true;
  }

  if (isSalesReport(reportId) && !featureAccess.canAccessSales) {
    return false;
  }

  if (isPurchasingReport(reportId) && !featureAccess.canAccessPurchasing) {
    return false;
  }

  return true;
};

/**
 * Utility to check if a user has access to a specific report based on permissions and feature access
 */
const hasReportAccess = (
  reportId: number,
  userPermissions: any[],
  isAdmin: boolean,
  featureAccess: ReportFeatureAccess,
  reportPermission?: any
): boolean => {
  const allowedPermissions = reportPermission ? [reportPermission] : [];

  // Check permissions first
  if (!hasPermission(allowedPermissions, userPermissions, isAdmin)) {
    return false;
  }

  // Check feature access for the report category
  return hasFeatureAccessForReport(reportId, featureAccess);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ReportsPage: ReportsPagePageCmp = (props: ReportsPageProps) => {
  const dispatch = useDispatch();
  const { permissions: userPermissions, isAdmin } = useSelector(getActiveUser);
  const featureAccess = useFeatureAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [urlQuery, extendUrlQuery] = useUrlQueryObject(navigate, location);

  const [activeId, setActiveId] = useState<number | null>(null);
  const [search, setSearch] = useState<string | null>(null);

  const reportsWithPermissions = useMemo(() => {
    const sortedReports = _.sortBy(reportList, 'title');

    return sortedReports.filter((r) =>
      hasReportAccess(
        r.id,
        userPermissions,
        isAdmin,
        featureAccess,
        r.permission
      )
    );
  }, [
    userPermissions,
    isAdmin,
    featureAccess.canAccessSales,
    featureAccess.canAccessPurchasing,
    featureAccess.canViewReorderReport,
  ]);

  const filteredReportList = useMemo(() => {
    if (!search) {
      return reportsWithPermissions;
    }

    const q = search.toLowerCase();
    return reportsWithPermissions.filter((r) => {
      const inTitle = r.title.toLowerCase().includes(q);
      const inDesc = (r.description ?? '').toLowerCase().includes(q);
      const inId = String(r.id).toLowerCase().includes(q);
      return inTitle || inDesc || inId;
    });
  }, [search, reportsWithPermissions]);

  useEffect(() => {
    // Lint skip to be removed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    dispatch(fetchLocations());
    // @ts-ignore
    dispatch(fetchTags());

    // @ts-ignore
    dispatch(fetchRepresentatives());
    // @ts-ignore
    dispatch(fetchVendors());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // navigate back to reports page if user doesn't have report permission
  useEffect(() => {
    const activeHasPermission =
      reportList.filter(
        (r) =>
          hasReportAccess(
            r.id,
            userPermissions,
            isAdmin,
            featureAccess,
            r.permission
          ) && r.id === activeId
      ).length > 0;
    if (!activeHasPermission && activeId) {
      setActiveId(null);
      navigate(concatRoute(Routes.ReportsRouter, Routes.ReportsPage), {
        replace: true,
      });
    }
  }, [
    activeId,
    userPermissions,
    isAdmin,
    featureAccess.canAccessSales,
    featureAccess.canAccessPurchasing,
    featureAccess.canViewReorderReport,
  ]);

  useEffect(() => {
    // Update activeId on url change
    if (urlQuery.activeId) {
      setActiveId(urlQuery.activeId);
      return;
    }
    setActiveId(null);
  }, [urlQuery]);

  const handleReportClicked = useCallback(
    (id: number) => {
      setActiveId(id);
      extendUrlQuery({ activeId: id });
    },
    [extendUrlQuery]
  );

  const handleReportsOnClose = useCallback(() => {
    setActiveId(null);
    extendUrlQuery({ activeId: null });
  }, [extendUrlQuery]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setSearch(value || null);
    },
    []
  );

  return (
    <Page>
      <Box
        sx={{
          alignItems: 'center',
          padding: '16px',
          backgroundColor: `${colorPalette.white}`,
          borderRadius: '10px',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        <FBOSearchInput
          type="text"
          placeholder="Search"
          value={search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <icons.MagnifyingGlass />
              </InputAdornment>
            ),
            sx: { paddingLeft: '8px' },
          }}
          onChange={handleSearchChange}
          dataQa="quick-search-input"
        />
      </Box>

      <PaperSlidingLayout shown={Boolean(activeId)}>
        <ReportsPageTable
          items={filteredReportList}
          activeId={activeId}
          onReportClick={handleReportClicked}
          hasSearchTerm={!!search}
          totalReportsAvailable={reportsWithPermissions.length}
        />
        <ReportsDetailsCard
          activeId={activeId}
          onClose={handleReportsOnClose}
        />
      </PaperSlidingLayout>
    </Page>
  );
};

ReportsPage.route = Routes.ReportsPage;

export default ReportsPage;
